import * as DatabaseService from '@/services/Database.service';
import * as AuthMutations from '@/store/mutations/Auth.mutations';
import * as UserMutations from '@/store/mutations/User.mutations';
import * as WorkspaceMutations from '@/store/mutations/Workspace.mutations';
import * as UserActions from '@/store/actions/User.actions';
import { dictionaryFromDocumentArray } from '@/helpers/dataHelpers';

const receiveUsers = (state, payload) => {
  state.users = {
    ...state.users,
    ...payload.users,
  };
  state.isUserLoading = false;
};

const getInitialState = () => ({
  users: {},
  isUserLoading: false,
  isWritingData: false,
});

const state = getInitialState();

const getters = {
  userIds(state) {
    return Object.keys(state.users);
  },
  loggedInUser(state, getters, rootState) {
    return state.users[rootState.auth.loggedInUserId] || {};
  },
  isLoggedInUserWorkspaceOwner(state, getters, rootState) {
    if (!getters.activeWorkspace) return false;

    return rootState.auth.loggedInUserId === getters.activeWorkspace.ownerId;
  },
};

const mutations = {
  [UserMutations.USER_REQUEST_DATA](state) {
    state.isUserLoading = true;
  },
  [UserMutations.USER_BEGIN_WRITE_DATA](state) {
    state.isWritingData = true;
  },
  [WorkspaceMutations.RECEIVE_WORKSPACES]: receiveUsers,
  [UserMutations.RECEIVE_USERS]: receiveUsers,
  [UserMutations.USER_COMPLETE_WRITE_DATA](state) {
    state.isWritingData = false;
  },
  [UserMutations.SET_THEME_PREFERENCE](state, payload) {
    const user = state.users[payload.userId];
    if (user) {
      state.users = {
        ...state.users,
        [payload.userId]: { ...user, themePreference: payload.themePreference },
      };
    }
  },
  [UserMutations.SET_OOO_RANGES](state, payload) {
    const user = state.users[payload.userId];
    if (user) {
      state.users = {
        ...state.users,
        [payload.userId]: { ...user, oooRanges: payload.oooRanges },
      };
    }
  },
  [AuthMutations.LOG_OUT](state) {
    Object.assign(state, getInitialState());
  },
};

const actions = {
  async [UserActions.FETCH_WORKSPACE_USERS_BY_IDS](context, payload) {
    context.commit(UserMutations.USER_REQUEST_DATA);
    return new Promise(async (resolve, reject) => {
      try {
        const userDocuments = await Promise.all(
          DatabaseService.getWorkspaceUsersByIds(payload.userIds, payload.workspaceId),
        );
        const shapedUserDocuments = dictionaryFromDocumentArray(userDocuments);
        context.commit(UserMutations.RECEIVE_USERS, { users: shapedUserDocuments });
        resolve();
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
        reject();
      }
    });
  },
  async [UserActions.FETCH_WORKSPACE_USERS](context, payload) {
    context.commit(UserMutations.USER_REQUEST_DATA);
    return new Promise(async (resolve, reject) => {
      try {
        const { docs: userDocuments } = await DatabaseService.getWorkspaceUsers(payload.workspaceId);
        const shapedUserDocuments = dictionaryFromDocumentArray(userDocuments);
        context.commit(UserMutations.RECEIVE_USERS, { users: shapedUserDocuments });
        resolve();
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
        reject();
      }
    });
  },
  async [UserActions.CREATE_USER](context, payload) {
    context.commit(UserMutations.USER_BEGIN_WRITE_DATA);
    return new Promise(async (resolve, reject) => {
      try {
        const newUserRef = await DatabaseService.addNewUser(payload.user);
        context.commit(UserMutations.USER_COMPLETE_WRITE_DATA);
        await context.dispatch(UserActions.FETCH_WORKSPACE_USERS_BY_IDS, { userIds: [newUserRef.id] });
        resolve(newUserRef);
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
        reject(err);
      }
    });
  },
  async [UserActions.UPDATE_USER_PROFILE](context, payload) {
    context.commit(UserMutations.USER_BEGIN_WRITE_DATA);
    const { workspaceId } = payload;
    return new Promise(async (resolve, reject) => {
      try {
        await DatabaseService.updateUserById(payload.userId, payload.updatedUser, workspaceId);
        context.commit(UserMutations.USER_COMPLETE_WRITE_DATA);
        await context.dispatch(UserActions.FETCH_WORKSPACE_USERS_BY_IDS, { userIds: [payload.userId], workspaceId });
        resolve();
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
        reject(err);
      }
    });
  },
  async [UserActions.UPDATE_USER_LOGIN_TIMESTAMP](context, payload) {
    context.commit(UserMutations.USER_BEGIN_WRITE_DATA);
    return new Promise(async (resolve, reject) => {
      try {
        await DatabaseService.updateUserLoginTimestamp(payload.userId, payload.workspaceId);
        context.commit(UserMutations.USER_COMPLETE_WRITE_DATA);
        resolve();
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
        reject(err);
      }
    });
  },
  async [UserActions.UPDATE_THEME_PREFERENCE](context, payload) {
    // Optimistically update local state so the theme switches immediately
    context.commit(UserMutations.SET_THEME_PREFERENCE, {
      userId: payload.userId,
      themePreference: payload.themePreference,
    });
    return new Promise(async (resolve, reject) => {
      try {
        await DatabaseService.updateUserById(
          payload.userId,
          { themePreference: payload.themePreference },
          payload.workspaceId,
        );
        resolve();
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
        reject(err);
      }
    });
  },
  async [UserActions.UPDATE_OOO_RANGES](context, payload) {
    // Optimistically update local state so OOO indicators reflect immediately
    context.commit(UserMutations.SET_OOO_RANGES, {
      userId: payload.userId,
      oooRanges: payload.oooRanges,
    });
    return new Promise(async (resolve, reject) => {
      try {
        await DatabaseService.updateUserOOORanges(payload.userId, payload.workspaceId, payload.oooRanges);
        resolve();
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
        reject(err);
      }
    });
  },
  async [UserActions.ARCHIVE_USER](context, payload) {
    context.commit(UserMutations.USER_BEGIN_WRITE_DATA);
    return new Promise(async (resolve, reject) => {
      const { workspaceId } = payload;
      try {
        await DatabaseService.archiveUser(payload.userId, payload.teamIds, workspaceId);
        context.commit(UserMutations.USER_COMPLETE_WRITE_DATA);

        await context.dispatch(
          UserActions.FETCH_WORKSPACE_USERS_BY_IDS,
          { userIds: [payload.userId], workspaceId },
        );
        resolve();
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
        reject(err);
      }
    });
  },
};

export default {
  state,
  getters,
  mutations,
  actions,
};
